import { useEffect, useRef, useState } from "react";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { useLazyFetchMeQuery } from "@/pages/Public/redux/auth.api";
import { User } from "@/pages/Public/pages/Login/schema/login";

import { useCreateUnipileSessionMutation } from "@/pages/Private/redux/unipile/unipile.api";

export type UnipileConnectType = "linkedin" | "email";

interface UseUnipileConnectParams {
	type: UnipileConnectType;
	onConnected?: () => void;
}

const isConnected = (type: UnipileConnectType, me: User): boolean =>
	type === "linkedin"
		? Boolean(me.unipile?.isLoggedIn)
		: Boolean(me.emails?.find((email) => email.type === "email"));

/**
 * Encapsulates the Unipile connect flow shared by the Account page and the
 * dashboard: it creates a Unipile session, opens the provider login in a new
 * tab and polls `fetchMe` until the account is connected.
 */
export const useUnipileConnect = ({ type, onConnected }: UseUnipileConnectParams) => {
	const { token } = useAppSelector(authSelector);

	const [fetchMe] = useLazyFetchMeQuery();
	const [createUnipileSession, { isLoading }] = useCreateUnipileSessionMutation();

	const [error, setError] = useState<string | null>(null);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [startValidation, setStartValidation] = useState<boolean>(false);

	const validatingRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		return () => {
			if (validatingRef.current) {
				clearInterval(validatingRef.current);
			}
		};
	}, []);

	const connect = async () => {
		try {
			const response = await createUnipileSession({
				type,
				successRedirectUrl: window.location.href || undefined,
			}).unwrap();

			if (response.url) {
				setStartValidation(true);
				window.open(response.url, "_blank");

				validatingRef.current = setInterval(async () => {
					if (!token) {
						return;
					}

					const me = await fetchMe(token).unwrap();

					if (isConnected(type, me) && validatingRef.current) {
						clearInterval(validatingRef.current);
						validatingRef.current = null;
						onConnected?.();
					}
				}, 1000);

				setShowConfirmModal(false);
			}
		} catch (error) {
			setError(((error as FetchBaseQueryError).data as { message: string }).message);
		}
	};

	return {
		isLoading,
		error,
		setError,
		showConfirmModal,
		setShowConfirmModal,
		startValidation,
		setStartValidation,
		connect,
	};
};
